import React, { useEffect, useRef, useState } from "react";
import { BellIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { editNotificationToRead } from "../../redux/reducer/notificationReducer";
import { getAddressesByUserId } from "../../redux/reducer/userAddressReducer";
import notificationSound from "../../assets/Notification/mixkit-correct-answer-tone-2870.wav";

const NotificationButton = ({ notification }) => {
  const iconSize = "lg:w-7 w-6 lg:h-7 h-6";
  const audioRef = useRef(null);
  const previousUnreadRef = useRef(0);

  const notificationUnread = notification?.filter((n) => !n.isRead).length;

  useEffect(() => {
    const storedUnread =
      parseInt(localStorage.getItem("notificationUnread")) || 0;
    if (notificationUnread > 0 && storedUnread === 0) {
      audioRef.current
        ?.play()
        .catch((err) => console.error("Autoplay error:", err));
    }

    localStorage.setItem("notificationUnread", notificationUnread);
    previousUnreadRef.current = storedUnread;
  }, []);

  useEffect(() => {
    const prev = previousUnreadRef.current;
    if (notificationUnread > prev) {
      audioRef.current
        ?.play()
        .catch((err) => console.error("Autoplay error:", err));
    }

    localStorage.setItem("notificationUnread", notificationUnread);
    previousUnreadRef.current = notificationUnread;
  }, [notificationUnread]);

  return (
    <div className="relative inline-block group">
      <audio ref={audioRef} src={notificationSound} preload="auto" />
      <BellIcon
        className={`${iconSize} ${
          notificationUnread ? "text-red-400" : "text-white"
        } cursor-pointer duration-300 group-hover:text-custom-yellow`}
      />
      {notificationUnread > 0 && (
        <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-red-400 duration-300 bg-black rounded-full group-hover:text-custom-yellow">
          {notificationUnread}
        </span>
      )}
    </div>
  );
};

const NotificationList = ({ notification, userRole }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const [direction, setDirection] = useState(
    localStorage.getItem("language") === "en" ? "rtl" : "ltr"
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const language = localStorage.getItem("language");
    setDirection(language === "ar" ? "rtl" : "ltr");
  }, [localStorage.getItem("language")]);

  const handleMarkAsRead = (notiId) => {
    dispatch(editNotificationToRead({ notiId, data: { isRead: true } })).then(
      () => {
        dispatch(getAddressesByUserId()).then(() => {
          const updatedUnread = notification?.filter((n) => !n.isRead).length;

          localStorage.setItem("notificationUnread", updatedUnread);
        });
      }
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getAddressesByUserId()).then((res) => {
        const updatedUnread = notification?.filter((n) => !n.isRead).length;
        const prevUnread =
          parseInt(localStorage.getItem("notificationUnread")) || 0;

        localStorage.setItem("notificationUnread", updatedUnread);
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative inline-block mt-2"
      onClick={toggleDropdown}
      ref={buttonRef}
    >
      <NotificationButton notification={notification} />

      {isOpen && (
        <div
          className={`absolute z-[9999999] mt-1 text-black bg-white rounded-[3px] shadow-lg start-0 w-56 md:w-96 transition-opacity duration-500 ${
            isOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          ref={dropdownRef}
        >
          <div
            className={`absolute w-0 h-0 border-b-8 border-l-8 border-r-8 ${
              direction === "ltr" ? "right-2" : "left-2"
            } -top-2 border-l-transparent border-r-transparent border-b-white`}
          />
          <div className="p-2" dir={direction === "ltr" ? "ltr" : "rtl"}>
            <h3 className="mb-2 text-xs font-semibold md:text-lg">
              {t("notifications.title")}
            </h3>

            <div className="max-h-[45vh] overflow-y-auto">
              <ul className="relative space-y-2 text-black">
                {notification?.length > 0 ? (
                  notification
                    .slice()
                    .reverse()
                    .map((notification) => (
                      <li
                        key={notification._id}
                        className={`relative group p-2 text-xs text-black flex flex-col duration-300 shadow-md cursor-pointer md:text-sm ${
                          notification.isRead
                            ? "hover:bg-gray-100"
                            : "bg-yellow-200 hover:bg-custom-yellow"
                        }`}
                        onClick={() => handleMarkAsRead(notification._id)}
                      >
                        <a className="flex-1" href={`${notification.link}`}>
                          <div className="mb-1 font-bold">
                            {i18n.language === "ar"
                              ? notification?.title?.ar
                              : notification?.title?.en}
                          </div>
                          <div className="leading-snug text-gray-700 line-clamp-2">
                            {i18n.language === "ar"
                              ? notification?.desc?.ar
                              : notification?.desc?.en}
                          </div>
                        </a>
                      </li>
                    ))
                ) : (
                  <li className="text-gray-500">
                    {t("notifications.noNewNotifications")}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationList;
